import { startOfDay, differenceInCalendarDays, subDays } from 'date-fns';
import { tz } from '@date-fns/tz';
import type { DateRange, Timezone } from '@/features/report-snapshots/api/schema';
import type { I18n } from '@lingui/core';

interface ComputedRange {
  start: Date;
  end: Date;
}

function toStartOfDayInZone(date: Date, zoneName: string): Date {
  return startOfDay(date, { in: tz(zoneName) });
}

export function computeDateRange(dateRange: DateRange, timezone: Timezone): ComputedRange | null {
  if (!dateRange) return null;
  const zoneName = timezone.ianaName;

  // If explicit start/end are provided, respect them
  if (dateRange.start && dateRange.end) {
    // Construct Date objects in the target timezone day boundaries
    const start = toStartOfDayInZone(new Date(dateRange.start), zoneName);
    const end = toStartOfDayInZone(new Date(dateRange.end), zoneName);
    return { start, end };
  } else {
    return null;
  }
}

/**
 * Calculates the number of days in a date range (inclusive).
 * Returns null if the date range cannot be computed.
 */
export function getDateRangeDayCount(dateRange: DateRange, timezone: Timezone): number | null {
  const range = computeDateRange(dateRange, timezone);
  if (!range) return null;

  return differenceInCalendarDays(range.end, range.start) + 1;
}

/**
 * Calculates comparison date ranges by subtracting the duration of the current range.
 * Returns null if the date range cannot be computed.
 *
 * @returns An object with comparisonStart and comparisonEnd dates, or null
 */
export function getComparisonDateRanges(
  dateRange: DateRange,
  timezone: Timezone,
): { comparisonStart: Date; comparisonEnd: Date } | null {
  const currentRange = computeDateRange(dateRange, timezone);
  if (!currentRange) return null;

  const durationInDays = differenceInCalendarDays(currentRange.end, currentRange.start) + 1;
  const comparisonStart = subDays(currentRange.start, durationInDays);
  const comparisonEnd = subDays(currentRange.end, durationInDays);

  return { comparisonStart, comparisonEnd };
}

/**
 * Formats a date range as a short label without year (e.g., "01 Jan - 15 Jan").
 * If the start and end dates are the same, only the start date is shown.
 * Returns null if the date range cannot be computed.
 */
export function formatDateRangeWithoutYear(
  dateRange: DateRange,
  timezone: Timezone,
  i18n: I18n,
): string | null {
  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    timeZone: timezone.ianaName,
  };
  return _formatDateRange(dateRange, timezone, dateFormatOptions, i18n);
}

/**
 * Formats a date range as a short label with year (e.g., "01 Jan, 2025 - 15 Jan, 2025").
 * If the start and end dates are the same, only the start date is shown.
 * Returns null if the date range cannot be computed.
 */
export function formatDateRangeWithYear(
  dateRange: DateRange,
  timezone: Timezone,
  i18n: I18n,
): string | null {
  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: timezone.ianaName,
  };
  return _formatDateRange(dateRange, timezone, dateFormatOptions, i18n);
}

function _formatDateRange(
  dateRange: DateRange,
  timezone: Timezone,
  dateFormatOptions: Intl.DateTimeFormatOptions,
  i18n: I18n,
): string | null {
  const range = computeDateRange(dateRange, timezone);
  if (!range) return null;

  const start = i18n.date(range.start, dateFormatOptions);
  const end = i18n.date(range.end, dateFormatOptions);

  if (start === end) {
    return start;
  }

  return `${start} - ${end}`;
}
